import DOMPurify from 'dompurify'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import * as yup from 'yup'

import { RootState } from 'src/utilities/store'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { getErrorMsgs } from 'src/pages/DynamicPage/functions'
import FormInput from 'src/pages/DynamicPage/components/FormInput'
import { useTranslation } from 'react-i18next'
import Button from 'src/pages/DynamicPage/components/Button'
import Error from 'src/pages/DynamicPage/components/Error/index'
import { PHONE_NUMBER } from 'src/utilities/constants'

export interface CustomFormValues {
  fullName: string
  email: string
  phone: string
  comment: string
}

interface CustomHTMLProps {
  html: string
  loading?: boolean
  addContactForm?: boolean
  onSubmit?: (data: CustomFormValues) => Promise<{ error: boolean } | undefined>
}

interface ContainerProps {
  dynamicStyles: string
}

const schema: yup.ObjectSchema<CustomFormValues> = yup.object({
  fullName: yup.string().required(),
  email: yup.string().email().required(),
  phone: yup
    .string()
    .required()
    .test(PHONE_NUMBER, (phone) => {
      return phone.startsWith('+3706') && phone.length === 12
    }),
  comment: yup.string().required(),
})

const DEFAULT_APPLICATION_FORM_VALUES: CustomFormValues = {
  fullName: '',
  email: '',
  phone: '',
  comment: '',
}

const InputsContainer = styled.form`
  display: grid;
  row-gap: 1.25rem;
  max-width: 28.125rem;
  margin: 0 auto;
  width: 100%;

  @media ${({ theme }) => theme.queries.mobile} {
    max-width: none;
  }
`

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(1fr);
  gap: 1.25rem;

  @media ${({ theme }) => theme.queries.mobile} {
    grid-template-columns: 1fr;
  }
`

const Container = styled.div<ContainerProps>`
  .custom-html {
    ${({ dynamicStyles }) => dynamicStyles}

    font-family: ${({ theme }) => theme.fontFamilies.primary};

    h2 {
      font-family: ${({ theme }) => theme.fontFamilies.secondary};
    }
  }
`

const CustomHTML = ({ html, loading, addContactForm, onSubmit }: CustomHTMLProps) => {
  const websiteData = useSelector((state: RootState) => state.website.websiteData)

  const sanitizedHTML = useMemo(() => {
    const clean = DOMPurify.sanitize(html, {
      ADD_ATTR: ['target', 'rel'],
      RETURN_DOM: true,
    }) as Element;

    const links = clean.querySelectorAll('a[href]')
    links.forEach((link) => {
      const href = link.getAttribute('href')
      if (href?.startsWith('http')) {
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
      }
    })

    return clean.innerHTML
  }, [html])

  const { t } = useTranslation()

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitted },
  } = useForm<CustomFormValues>({
    defaultValues: DEFAULT_APPLICATION_FORM_VALUES,
    resolver: yupResolver(schema),
  })

  const errorMsgs = getErrorMsgs(errors)

  const handleFormSubmit = handleSubmit(async (data) => {
    const res = await onSubmit?.(data)
    if (res?.error) {
      return
    }

    reset(DEFAULT_APPLICATION_FORM_VALUES)
  })

  useEffect(() => {
    if (!isSubmitted) {
      return
    }
  }, [isSubmitted])

  return (
    <>
      <Container dynamicStyles={websiteData?.generalStyle ?? ''}>
        <div className="custom-html" dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />
      </Container>
      {addContactForm && (
        <InputsContainer onSubmit={handleFormSubmit}>
          <FormInput
            type="text"
            name="fullName"
            placeholder={t('pages.dynamicPage.components.applicationForm.fullNamePlaceholder')}
            control={control}
            disabled={loading}
          />

          <FormInput
            type="email"
            name="email"
            placeholder={t('pages.dynamicPage.components.applicationForm.emailPlaceholder')}
            control={control}
            disabled={loading}
          />

          <FormInput
            type="text"
            name="phone"
            placeholder={t('pages.dynamicPage.components.applicationForm.phonePlaceholder')}
            control={control}
            disabled={loading}
          />

          <FormInput
            type="textarea"
            name="comment"
            placeholder={t('pages.dynamicPage.components.applicationForm.commentPlaceholder')}
            control={control}
            disabled={loading}
            height="11.25rem"
          />

          <ButtonsContainer>
            <Button type="submit" disabled={loading}>
              {t('pages.dynamicPage.components.applicationForm.sendButtonLabel')}
            </Button>
          </ButtonsContainer>

          {errorMsgs.map((errorMsg, index) => (
            <Error key={index} msg={errorMsg} />
          ))}
        </InputsContainer>
      )}
    </>
  )
}

export default CustomHTML
